"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FulfillmentOrderClient = void 0;
const base_client_1 = require("@deliverr/base-client");
const errors_1 = require("@deliverr/errors");
const client_auth_1 = require("@deliverr/client-auth");
const RemovalDetails_1 = require("./removals/RemovalDetails");
const TestOrderDetails_1 = require("./testOrders/TestOrderDetails");
const core_auth_1 = require("@deliverr/core-auth");
const FulfillmentOrderException_1 = require("./FulfillmentOrderException");
const OrderExceptions_1 = require("./OrderExceptions");
const VersionUtils_1 = require("./VersionUtils");
const commons_logs_1 = require("@deliverr/commons-logs");
const FULFILLMENT_ORDER_BASE_URL = process.env.FULFILLMENT_ORDER_BASE_URL;
/**
 * USE THIS CLIENT FOR ORDERS
 * This client contains order domain APIs which are housed in the
 * order-service. Consumers that use order domain APIs should
 * use this client instead of the OrdersClient, which contains
 * APIs from different domains that are housed in the deprecated
 * fulfillment-service.
 */
class FulfillmentOrderClient extends base_client_1.ApiClient {
    constructor(config) {
        const baseURL = `${config.baseURL || FULFILLMENT_ORDER_BASE_URL}`;
        if (!baseURL) {
            const errorMessage = "FULFILLMENT_ORDER_BASE_URL environment variable must be set";
            commons_logs_1.log.warn(errorMessage);
            throw new Error(errorMessage);
        }
        let authEngine = core_auth_1.AuthEngine.COGNITO;
        let tokenRefreshFn = config.tokenRefreshFn;
        if (config.authEngine === core_auth_1.AuthEngine.COGNITO_INTERNAL) {
            authEngine = core_auth_1.AuthEngine.COGNITO_INTERNAL;
            tokenRefreshFn = tokenRefreshFn !== null && tokenRefreshFn !== void 0 ? tokenRefreshFn : new client_auth_1.OAuthClientTokenBuilder(config).getTokenFn();
        }
        super(Object.assign(Object.assign({}, config), { baseURL,
            authEngine,
            tokenRefreshFn }));
    }
    /**
     * Returns the extended OrderSummary, including shipments, packages, and labels.
     * Returns undefined if the order does not exist.
     */
    async getOrder(orderId, versionNum, includeConstraints, includeDropshipDetails, includeReimbursements) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            return await this.get(`/${version}/workflow/order/${orderId}`, {
                includeConstraints,
                includeDropshipDetails,
                includeReimbursements,
            });
        }
        catch (error) {
            const ctx = { err: error, orderId };
            switch (error.response && error.response.status) {
                case 404:
                    commons_logs_1.log.info(ctx, "getOrderSummary: not found");
                    return undefined;
                case 504:
                    commons_logs_1.log.warn(ctx, errors_1.ApiError.NETWORK_ERROR);
                    throw new Error(errors_1.ApiError.NETWORK_ERROR);
                default:
                    commons_logs_1.log.error(ctx, "getOrderSummary: unknown error");
                    throw new Error(errors_1.ApiError.UNKNOWN_ERROR);
            }
        }
    }
    /**
     * Returns just the order data, without shipments, packages, or labels. Returns
     * undefined if the order does not exist.
     */
    async getOrderData(orderId, versionNum, includeConstraints, includeDropshipDetails) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            const fetchedOrder = await this.get(`/${version}/order/${orderId}`, {
                includeConstraints,
                includeDropshipDetails,
            });
            // We currently return OK instead of undefined when the order does not exist
            return (fetchedOrder === null || fetchedOrder === void 0 ? void 0 : fetchedOrder.id)
                ? Object.assign(Object.assign({}, fetchedOrder), { promisedDeliveryTime: (0, base_client_1.convertToDateIfNecessary)(fetchedOrder.promisedDeliveryTime) }) : undefined;
        }
        catch (error) {
            const ctx = { err: error, orderId };
            switch (error.response && error.response.status) {
                case 404:
                    commons_logs_1.log.info(ctx, "getDeliverrOrder: not found");
                    return undefined;
                case 504:
                    commons_logs_1.log.warn(ctx, errors_1.ApiError.NETWORK_ERROR);
                    throw new Error(errors_1.ApiError.NETWORK_ERROR);
                default:
                    commons_logs_1.log.error(ctx, "getDeliverrOrder: unknown error");
                    throw new Error(errors_1.ApiError.UNKNOWN_ERROR);
            }
        }
    }
    async getOrderByMarketplaceOrderId(marketplaceOrderId, sellerId, versionNum) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            return await this.get(`/${version}/workflow/order`, {
                marketplaceOrderId,
                sellerId,
            });
        }
        catch (error) {
            switch (error.response && error.response.status) {
                case 400:
                    throw new errors_1.DeliverrError({
                        code: OrderExceptions_1.OrderExceptions.INVALID_QUERY_PARAMETER,
                        payload: error,
                    });
                case 404:
                    return undefined;
                case 504:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.NETWORK_ERROR,
                        payload: error,
                    });
                default:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.UNKNOWN_ERROR,
                        payload: error,
                    });
            }
        }
    }
    async getOrderByTrackingCode(trackingCode, versionNum) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            return await this.get(`/${version}/workflow/order/tracking-code/${trackingCode}`);
        }
        catch (error) {
            switch (error.response && error.response.status) {
                case 404:
                    return undefined;
                default:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.UNKNOWN_ERROR,
                        payload: error,
                    });
            }
        }
    }
    /**
     * Must be called with either of the two groups of attributes:
     * 1. channelOrderId, channelIntegrationUUID
     * 2. channelOrderId, channelId, sellerId
     */
    async getOrderByChannelAttributes(channelOrderId, channelId, sellerId, channelIntegrationUUID, versionNum) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            return await this.get(`/${version}/order`, {
                channelOrderId,
                channelId,
                sellerId,
                sellerListingSolutionUUID: channelIntegrationUUID,
            });
        }
        catch (error) {
            switch (error.response.status) {
                case 404:
                    throw new errors_1.DeliverrError({
                        code: OrderExceptions_1.OrderExceptions.NOT_FOUND,
                        payload: error,
                    });
                case 400:
                    throw new errors_1.DeliverrError({
                        code: OrderExceptions_1.OrderExceptions.INVALID_QUERY_PARAMETER,
                        payload: error,
                    });
                default:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.NETWORK_ERROR,
                        payload: error,
                    });
            }
        }
    }
    async reprocessOrder(orderId, orderQueueEventType, versionNum) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            await this.post(`/${version}/order/${orderId}`, { orderQueueEventType });
        }
        catch (error) {
            switch (error.response.status) {
                case 404:
                case 500: // unfortunately the order API returns a 500 on NOT_FOUND
                    throw new errors_1.DeliverrError({
                        code: OrderExceptions_1.OrderExceptions.NOT_FOUND,
                        payload: error,
                    });
                case 400:
                    throw new errors_1.DeliverrError({
                        code: OrderExceptions_1.OrderExceptions.CANNOT_REPROCESS,
                        payload: error,
                    });
                default:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.NETWORK_ERROR,
                        payload: error,
                    });
            }
        }
    }
    async reprocessOrders(orderIds, orderQueueEventType, versionNum) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            return await this.post(`/${version}/orders/reprocess`, {
                orderIds,
                orderQueueEventType,
            });
        }
        catch (error) {
            throw new errors_1.DeliverrError({
                code: errors_1.ApiError.NETWORK_ERROR,
                payload: error,
            });
        }
    }
    async retryOrderProcessing(orderId, versionNum) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            return await this.post(`/${version}/order/${orderId}/retry`, {});
        }
        catch (error) {
            switch (error.response && error.response.status) {
                case 404:
                    throw new errors_1.DeliverrError({
                        code: OrderExceptions_1.OrderExceptions.NOT_FOUND,
                        payload: error,
                    });
                case 422:
                    throw new errors_1.DeliverrError({
                        code: OrderExceptions_1.OrderExceptions.INVALID_ORDER_STATUS,
                        payload: error,
                    });
                default:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.NETWORK_ERROR,
                        payload: error,
                    });
            }
        }
    }
    async createOrders(orders, versionNum) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            return await this.post(`/${version}/orders`, {
                orders,
            });
        }
        catch (error) {
            switch (error.response && error.response.status) {
                case 504:
                    commons_logs_1.log.warn({ err: error }, errors_1.ApiError.NETWORK_ERROR);
                    throw new Error(errors_1.ApiError.NETWORK_ERROR);
                default:
                    commons_logs_1.log.error({ err: error, orders }, "createOrders: unknown error");
                    throw new Error(errors_1.ApiError.UNKNOWN_ERROR);
            }
        }
    }
    async createOrder(order, versionNum) {
        var _a, _b;
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            return await this.post(`/${version}/order`, order);
        }
        catch (error) {
            const ctx = { order, err: error };
            switch (error.response && error.response.status) {
                case 400:
                    throw new Error((_b = (_a = error.response.data) === null || _a === void 0 ? void 0 : _a.error) !== null && _b !== void 0 ? _b : OrderExceptions_1.OrderExceptions.BAD_REQUEST);
                case 409:
                    commons_logs_1.log.info(ctx, "createOrder: Duplicate order");
                    throw new Error(OrderExceptions_1.OrderExceptions.DUPLICATE_ORDER);
                case 410:
                    commons_logs_1.log.info(ctx, "createOrder: Stockout");
                    throw new Error(OrderExceptions_1.OrderExceptions.STOCKOUT);
                case 504:
                    commons_logs_1.log.warn(ctx, errors_1.ApiError.NETWORK_ERROR);
                    throw new Error(errors_1.ApiError.NETWORK_ERROR);
                default:
                    commons_logs_1.log.error(ctx, "createOrder: unknown error");
                    throw new Error(errors_1.ApiError.UNKNOWN_ERROR);
            }
        }
    }
    async createOrderV2(order, versionNum) {
        var _a, _b, _c, _d;
        const version = (0, VersionUtils_1.getVersionAsPathString)(2, versionNum);
        try {
            return await this.post(`/${version}/order`, order);
        }
        catch (error) {
            const ctx = { order, err: error };
            switch (error.response && error.response.status) {
                case 400:
                    throw new FulfillmentOrderException_1.FulfillmentOrderException(this.createOrderStatus400ErrorsMapper((_a = error.response.data) === null || _a === void 0 ? void 0 : _a.error, ctx), error, (_b = error.response.data) === null || _b === void 0 ? void 0 : _b.error);
                case 409:
                    commons_logs_1.log.info(ctx, "createOrder: Duplicate order");
                    throw new FulfillmentOrderException_1.FulfillmentOrderException(OrderExceptions_1.OrderExceptions.DUPLICATE_ORDER, error);
                case 410:
                    commons_logs_1.log.info(ctx, "createOrder: Stockout");
                    throw new FulfillmentOrderException_1.FulfillmentOrderException(OrderExceptions_1.OrderExceptions.STOCKOUT, error);
                case 422:
                    commons_logs_1.log.info(ctx, "createOrder: Unknown sku");
                    throw new FulfillmentOrderException_1.FulfillmentOrderException(OrderExceptions_1.OrderExceptions.UNKNOWN_SKU, error);
                case 504:
                    commons_logs_1.log.warn(ctx, errors_1.ApiError.NETWORK_ERROR);
                    throw new FulfillmentOrderException_1.FulfillmentOrderException(errors_1.ApiError.NETWORK_ERROR, error);
                default:
                    // eslint-disable-next-line no-case-declarations
                    const errorType = ((_d = (_c = error.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.error) || errors_1.ApiError.UNKNOWN_ERROR;
                    commons_logs_1.log.error(`createOrder: ${errorType}`, order, error.response);
                    throw new FulfillmentOrderException_1.FulfillmentOrderException(errorType, error);
            }
        }
    }
    async createOrdersV3(bulkOrderCreateRequest, versionNum) {
        var _a, _b;
        const version = (0, VersionUtils_1.getVersionAsPathString)(3, versionNum);
        try {
            return await this.post(`/${version}/orders`, bulkOrderCreateRequest);
        }
        catch (error) {
            // eslint-disable-next-line no-case-declarations
            const errorType = ((_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.error) || errors_1.ApiError.UNKNOWN_ERROR;
            commons_logs_1.log.error(`createOrder: ${errorType}`, bulkOrderCreateRequest, error.response);
            throw new FulfillmentOrderException_1.FulfillmentOrderException(errorType, error);
        }
    }
    async updateShippingAddress(orderId, address, forceUpdate = false, isAllowlisted = false, versionNum) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            const body = { address, forceUpdate, isAllowlisted };
            return await this.put(`/${version}/order/${orderId}/address`, body);
        }
        catch (error) {
            const ctx = { err: error, orderId, forceUpdate };
            switch (error.response && error.response.status) {
                case 400:
                    commons_logs_1.log.info(ctx, "updateShippingAddress: order already shipped");
                    throw new Error(OrderExceptions_1.OrderExceptions.ORDER_SHIPPED);
                case 404:
                    commons_logs_1.log.info(ctx, "updateShippingAddress: order not found");
                    throw new Error(OrderExceptions_1.OrderExceptions.NOT_FOUND);
                case 422:
                    commons_logs_1.log.info(ctx, "updateShippingAddress: invalid address provided");
                    throw new Error(OrderExceptions_1.OrderExceptions.INVALID_ADDRESS);
                case 504:
                    commons_logs_1.log.warn(ctx, errors_1.ApiError.NETWORK_ERROR);
                    throw new Error(errors_1.ApiError.NETWORK_ERROR);
                default:
                    commons_logs_1.log.error(ctx, "updateShippingAddress: unknown error");
                    throw new Error(errors_1.ApiError.UNKNOWN_ERROR);
            }
        }
    }
    async partiallyFulfillOrder(orderId, versionNum) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            await this.put(`/${version}/workflow/order/${orderId}/items/`, {});
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
    async updateOrderCost(orderId, versionNum) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            await this.put(`/${version}/order/${orderId}/cost`, {});
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
    async updateReportedTime(orderId, reportedTime, versionNum) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            await this.put(`/${version}/order/${orderId}/reportedTime`, {
                reportedTime,
            });
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
    async updateStatus(orderId, maxPackageDeliverTime, versionNum) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            await this.put(`/${version}/order/update-status`, {
                orderId,
                maxPackageDeliverTime,
            });
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
    async updateOrder(orderId, address, items, versionNum, originConstraints) {
        var _a, _b;
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            return await this.put(`/${version}/order/${orderId}`, { address, items, originConstraints });
        }
        catch (error) {
            switch ((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) {
                case 400:
                    throw new errors_1.DeliverrError({
                        code: OrderExceptions_1.OrderExceptions.BAD_REQUEST,
                        payload: error,
                    });
                case 404:
                    throw new errors_1.DeliverrError({
                        code: OrderExceptions_1.OrderExceptions.NOT_FOUND,
                        payload: error,
                    });
                case 422:
                    throw new errors_1.DeliverrError({
                        code: (_b = error.response.data.error) !== null && _b !== void 0 ? _b : OrderExceptions_1.OrderExceptions.INVALID_ORDER_STATUS,
                        payload: error,
                    });
                default:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.NETWORK_ERROR,
                        payload: error,
                    });
            }
        }
    }
    async batchQueueOrders(orderIds, versionNum) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            await this.put(`/${version}/order/batch-queue-orders`, {
                orderIds,
            });
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
    async reportError(orderId, error, versionNum) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            await this.put(`/${version}/order/report-error`, {
                orderId,
                error,
            });
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
    async markShippedIfApplicable(orderId, allShipments, versionNum) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            await this.put(`/${version}/order/mark-shipped`, {
                orderId,
                shipments: allShipments,
            });
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
    async addErrorToOrder(orderId, error, versionNum) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            await this.put(`/${version}/order/add-error`, {
                orderId,
                error,
            });
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
    async addOrderTag(orderId, tag, versionNum) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            await this.put(`/${version}/order/add-tag`, {
                orderId,
                tag,
            });
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
    async addOrderWarning(orderId, warning, versionNum) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            await this.put(`/${version}/order/add-warning`, {
                orderId,
                warning,
            });
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
    async cancelOrder(orderId, cancelSource, error, versionNum) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            return await this.delete(`/${version}/workflow/order/${orderId}`, undefined, {
                cancelSource,
                error,
            });
        }
        catch (err) {
            if (err.response.data && err.response.data.error === OrderExceptions_1.OrderExceptions.FAILED_CANCEL_AT_WAREHOUSE) {
                commons_logs_1.log.error("cancelOrder: failed to cancel at warehouse");
                throw new Error(OrderExceptions_1.OrderExceptions.FAILED_CANCEL_AT_WAREHOUSE);
            }
            const ctx = { orderId, err };
            switch (err.response && err.response.status) {
                case 400:
                    commons_logs_1.log.info(ctx, "cancelOrder: order already shipped or delivered");
                    throw new Error(OrderExceptions_1.OrderExceptions.ORDER_SHIPPED);
                case 404:
                    commons_logs_1.log.info(ctx, "cancelOrder: order not found");
                    throw new Error(OrderExceptions_1.OrderExceptions.NOT_FOUND);
                case 504:
                    commons_logs_1.log.warn(ctx, errors_1.ApiError.NETWORK_ERROR);
                    throw new Error(errors_1.ApiError.NETWORK_ERROR);
                default:
                    commons_logs_1.log.error(ctx, "cancelOrder: unknown error");
                    throw new Error(errors_1.ApiError.UNKNOWN_ERROR);
            }
        }
    }
    async calculateOrderCost(order, versionNum) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            return await this.post(`/${version}/workflow/order/cost`, order);
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
    /**
     * The following APIs are for different order types which are currently located inside of the order-service but will eventually be migrated
     * to their own services which leverage the order-service. Until then, we'll keep the APIs here to have a single client for the order-service
     */
    async getTransferOrderDetails(orderId, versionNum) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            return await this.get(`/${version}/transfer-order-details/orderId/${orderId}`);
        }
        catch (error) {
            const ctx = { orderId, err: error };
            switch (error.response && error.response.status) {
                case 404:
                    commons_logs_1.log.info(ctx, "getTransferOrderDetails: not found");
                    return undefined;
                default:
                    commons_logs_1.log.error(ctx, "getTransferOrderDetails: unknown error");
                    throw new Error(errors_1.ApiError.UNKNOWN_ERROR);
            }
        }
    }
    async getTestOrderDetails(orderId, versionNum) {
        var _a;
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            return await this.get(`/${version}/test-order-details/${orderId}`);
        }
        catch (error) {
            switch ((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) {
                case 404:
                    throw new errors_1.DeliverrError({
                        code: TestOrderDetails_1.TestOrderDetailsError.NOT_FOUND,
                        payload: error,
                    });
                default:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.NETWORK_ERROR,
                        payload: error,
                    });
            }
        }
    }
    async getRemovalOrderDetails(orderId, versionNum) {
        var _a;
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            return await this.get(`/${version}/removal-details/${orderId}`);
        }
        catch (error) {
            switch ((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) {
                case 404:
                    throw new errors_1.DeliverrError({
                        code: RemovalDetails_1.RemovalDetailsError.NOT_FOUND,
                        payload: error,
                    });
                default:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.NETWORK_ERROR,
                        payload: error,
                    });
            }
        }
    }
    async getCustomsOptionsDetails(orderId, versionNum) {
        var _a;
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            return await this.get(`/${version}/order/${orderId}/customs-details`);
        }
        catch (error) {
            switch ((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) {
                case 400:
                    throw new errors_1.DeliverrError({
                        code: OrderExceptions_1.OrderExceptions.INVALID_ORDER_ID,
                        payload: error,
                    });
                case 404:
                    throw new errors_1.DeliverrError({
                        code: OrderExceptions_1.OrderExceptions.NOT_FOUND,
                        payload: error,
                    });
                default:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.NETWORK_ERROR,
                        payload: error,
                    });
            }
        }
    }
    async getDuties(orderId) {
        var _a;
        try {
            return await this.get(`/v1/order/${orderId}/duties`);
        }
        catch (error) {
            switch ((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) {
                case 400:
                    throw new errors_1.DeliverrError({
                        code: OrderExceptions_1.OrderExceptions.INVALID_ORDER_ID,
                        payload: error,
                    });
                case 404:
                    throw new errors_1.DeliverrError({
                        code: OrderExceptions_1.OrderExceptions.NOT_FOUND,
                        payload: error,
                    });
                default:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.NETWORK_ERROR,
                        payload: error,
                    });
            }
        }
    }
    async queueOrderForInventoryConsumption(orderId, versionNum) {
        const version = (0, VersionUtils_1.getVersionAsPathString)(1, versionNum);
        try {
            await this.put(`/${version}/order/${orderId}/batch-consume-inventory`, {});
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
    createOrderStatus400ErrorsMapper(errorType, ctx) {
        const allowedErrorTypes = [
            OrderExceptions_1.OrderExceptions.INVALID_DUTIES_MERCHANT_OF_RECORD_SKU_MAP,
            OrderExceptions_1.OrderExceptions.INVALID_ADDRESS,
            OrderExceptions_1.OrderExceptions.INVALID_SELLER,
            OrderExceptions_1.OrderExceptions.DROPSHIP_INVALID_DATA,
            OrderExceptions_1.OrderExceptions.DROPSHIP_INVALID_RETURN_ADDRESS,
            OrderExceptions_1.OrderExceptions.DROPSHIP_MARKETPLACE_NOT_ENABLED,
            OrderExceptions_1.OrderExceptions.DROPSHIP_OPTIONS_MISSING_REQUIRED_DATA,
            OrderExceptions_1.OrderExceptions.DROPSHIP_SELLER_NOT_ENABLED,
        ];
        const errorToThrow = allowedErrorTypes.includes(errorType) ? errorType : OrderExceptions_1.OrderExceptions.BAD_REQUEST;
        commons_logs_1.log.info(ctx, errorType);
        return errorToThrow;
    }
}
exports.FulfillmentOrderClient = FulfillmentOrderClient;
//# sourceMappingURL=FulfillmentOrderClient.js.map